import React, { useEffect, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  TextField,
  Menu,
  MenuItem,
} from "@mui/material";
import DatePickerField from "../../common/DatePickerDashboard";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import {
  searchBotDownloadLogsReportAction,
  searchBotLogsReportAction,
} from "../../redux/actions/botAction";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Loader from "../loader/loader";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import { useTheme, useMediaQuery } from '@mui/material';
import NoDataFound from "../../common/NoDataFound";
import MoreVertIcon from '@mui/icons-material/MoreVert';
 
const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 20,
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
  },
  table: {
    minWidth: 650,
  },
  tableCellData: {
    textAlign: "center",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f1f1f1",
    },
  },
  iconButton: {
    marginRight: 8,
    "&:hover": {
      backgroundColor: "#e0e0e0",
    },
  },
  downloadButton: {
    backgroundColor: "#4CAF50",
    color: "white",
    marginLeft: 16,
    "&:hover": {
      backgroundColor: "#45a049",
    },
  }, 
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  tableContainer: {
    borderRadius: 8,
    boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.1)",
  },
  tableCell: {
    fontWeight: '700 !important',
    backgroundColor: '#1565c0 !important',
    color: '#ffffff !important',
    fontFamily: '"Poppins", sans-serif !important',
    textAlign: "center !important",
    padding: '10px !important',
  },
  tableRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
    '& td, & th': {
      padding: theme.spacing(1.5),
      fontFamily: '"Poppins", sans-serif',
    }
  },
}));
 
function BotReport() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchBot = useSelector((state) => state.bot.searchBotlogs);
  const searchBotDownload = useSelector((state) => state.bot.searchBotDownloadLogs);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  
  
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const handleDownloadClick = (event) => setDownloadAnchorEl(event.currentTarget);
  const handleDownloadClose = () => setDownloadAnchorEl(null);
  
  
  const [fromDateInput, setFromDateInput] = useState(null);
  const [toDateInput, setToDateInput] = useState(null);
  
  const [hasSearched, setHasSearched] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [botData, setBotData] = useState([]);
  const searchreport = searchBot.content || [];
  const [isLoading, setIsLoading] = useState(false);
  const searchReportDownload = searchBotDownload.content || [];

  
  const defaultFromDate = null;
  const defaultToDate = null;

  
  const [formData, setFormData] = useState({
    fromDate: '',
    toDate: ''
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
 
  const count = hasSearched ? searchBot.totalElements : 0;

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    if (errorMsg) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
    }
  }
 
  useEffect(() => {
    if(count > 0) {
      dispatch(
        searchBotDownloadLogsReportAction(
          {
            fromDate: formData.fromDate,
            toDate: formData.toDate,
            search,
          },
          0,
          count
        )
      );
    }
  }, [dispatch, page, rowsPerPage, count, formData.fromDate, formData.toDate, search]);
 
  useEffect(() => {
    if (hasSearched) {
      setBotData(getBotData(searchreport));
    }
  }, [searchBot, hasSearched, searchreport]);
 
  const handleSearch = async () => {
  const hasDateRange = formData.fromDate && formData.toDate;
  const hasSearchTerm = search.trim().length > 0;

  if (!hasDateRange && !hasSearchTerm) {
    alert("Please select From/To Date or enter a Search Term.");
    return;
  }

  setIsLoading(true);
  setHasSearched(false);
  setPage(0);

  try {
    await dispatch(
      searchBotLogsReportAction(
        {
          fromDate: formData.fromDate,
          toDate: formData.toDate,
          search,
        },
        0,
        rowsPerPage
      )
    );
    setHasSearched(true);
  } finally {
    setIsLoading(false);
  }
};

  
  const resetSearch = () => {
    setHasSearched(false);
    setSearch("");
    setBotData([]);
    
   
    setFromDateInput(null);
    setToDateInput(null);
    
   
    const resetFormData = {
      fromDate: defaultFromDate ? defaultFromDate.format('MM-DD-YYYY') : '',
      toDate: defaultToDate ? defaultToDate.format('MM-DD-YYYY') : '',
    };
    
    setFormData(resetFormData);
    setFieldErrors({});
    setTouchedFields({});

    setPage(0);
  };
 
  
  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
    if (fieldName === 'fromDate') {
      setFromDateInput(value ? dayjs(value, 'MM-DD-YYYY') : null);
    } else if (fieldName === 'toDate') {
      setToDateInput(value ? dayjs(value, 'MM-DD-YYYY') : null);
    }

   
    if (fieldErrors.general || fieldErrors.dateRange) {
      setFieldErrors({});
    }
  };
 
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
 
  const handleSort = (field) => {
    const isAsc = sortField === field && sortOrder === "asc";
    setSortField(field);
    setSortOrder(isAsc ? "desc" : "asc");
  };

 
  const handleDownloadCSV = () => {
    const dataToExport = searchReportDownload.map(row => ({
      "Carrier Name": row.carrierName || '',
      "Process Master Name": row.botProcessMasterName || '',
      "Track Date": row.trackingDate || '',
      "Policy Number": row.policyNumber || '',
      "Sub Process Name": row.botSubProcessName || '',
      "Text Successful": row.resultText || '',
      "Result Successful": row.resultTask || '',
      "Date Created": row.createdAt ? dayjs(row.createdAt).format('MM-DD-YYYY, hh:mm:ss A') : ''
    }));

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        Object.keys(dataToExport[0]).join(','), 
        ...dataToExport.map(row => Object.values(row).map(val => `"${val}"`).join(','))
      ].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Bot_Report_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleDownloadClose();
  };

  
  const handleDownloadExcel = () => {
    const dataToExport = searchReportDownload.map(row => ({
      "Carrier Name": row.carrierName || '',
      "Process Master Name": row.botProcessMasterName || '',
      "Track Date": row.trackingDate || '',
      "Policy Number": row.policyNumber || '',
      "Sub Process Name": row.botSubProcessName || '',
      "Text Successful": row.resultText || '',
      "Result Successful": row.resultTask || '',
      "Date Created": row.createdAt ? dayjs(row.createdAt).format('MM-DD-YYYY, hh:mm:ss A') : ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bot Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `Bot_Report_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
    handleDownloadClose();
  };

  
  const handleDownloadPDF = () => {
    const doc = new jsPDF('landscape');
    const tableColumn = [
      "Carrier Name", "Process Master Name", "Track Date", "Policy Number", 
      "Sub Process Name", "Text Successful", "Result Successful", "Date Created"
    ];

    const tableRows = searchReportDownload.map(row => [
      row.carrierName || '',
      row.botProcessMasterName || '',
      row.trackingDate || '',
      row.policyNumber || '',
      row.botSubProcessName || '',
      row.resultText || '',
      row.resultTask || '',
      row.createdAt ? dayjs(row.createdAt).format('MM-DD-YYYY, hh:mm:ss A') : ''
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [246, 194, 30] },
      margin: { top: 20 },
      didDrawPage: function (data) {
        
        doc.setFontSize(16);
        doc.text('Bot Activity Report', 15, 15);
      }
    });

    doc.save(`Bot_Report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
    handleDownloadClose();
  };

  
  const downloadPDF = async () => {
    try {
      setIsDownloading(true);
      const allData = searchReportDownload;
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margins = 15;
 
      const hiddenDiv = document.createElement('div');
      hiddenDiv.style.position = 'absolute';
      hiddenDiv.style.left = '-9999px';
      hiddenDiv.style.top = '-9999px';
 
      pdf.setFontSize(16);
      pdf.text('Bot Report', margins, margins);
      pdf.setFontSize(12);
 
      const rowHeight = 12;
      const headerHeight = 25;
      const footerHeight = 10;
      const availableHeight = pageHeight - margins * 2 - headerHeight - footerHeight;
      const rowsPerPdfPage = Math.floor(availableHeight / rowHeight);
 
      const tableStyles = {
        fontSize: 10,
        font: 'Poppins',
        headerBgColor: '#EAC86B',
        headerTextColor: '#000',
        rowBgColor: '#FFF2CF',
        cellTextColor: '#000',
        borderColor: '#CECECE',
        cellPadding: 5,
        textAlign: 'center',
      };
 
      const createTableHeader = () => {
        return `
          <tr style="background-color: ${tableStyles.headerBgColor}; color: ${tableStyles.headerTextColor}; font-family: ${tableStyles.font}; font-weight: bold;">
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Carrier Name</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Process Master Name</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Track Date</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Policy Number</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Sub Process Name</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Text Successful</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Result Successful</th>
            <th style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">Date Created</th>
          </tr>
        `;
      };
 
      const createTableRow = (row, index) => {
        const rowBgColor = index % 2 === 0 ? tableStyles.rowBgColor : 'transparent';
        return `
          <tr style="background-color: ${rowBgColor}; font-family: ${tableStyles.font};">
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.carrierName || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.botProcessMasterName || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.trackingDate || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.policyNumber || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.botSubProcessName || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.resultText || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.resultTask || ''}</td>
            <td style="padding: ${tableStyles.cellPadding}px; border: 1px solid ${tableStyles.borderColor}; text-align: ${tableStyles.textAlign}">${row.createdAt ? dayjs(row.createdAt).format('MM-DD-YYYY, hh:mm:ss A') : ''}</td>
          </tr>
        `;
      };
 
      for (let i = 0; i < allData.length; i += rowsPerPdfPage) {
        if (i > 0) pdf.addPage();
 
        const pageData = allData.slice(i, i + rowsPerPdfPage);
        const tableHtml = `
             <table style="width: 100%; border-collapse: collapse; font-size: ${tableStyles.fontSize}px;">
          ${createTableHeader()}
          ${pageData.map((row, index) => createTableRow(row, i + index)).join('')}
        </table>
        `;
 
        hiddenDiv.innerHTML = tableHtml;
        document.body.appendChild(hiddenDiv);
 
        const canvas = await html2canvas(hiddenDiv, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff'
        });
 
        document.body.removeChild(hiddenDiv);
 
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pageWidth - (margins * 2);
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
 
        pdf.addImage(imgData, 'PNG', margins, i === 0 ? margins + 15 : margins, pdfWidth, pdfHeight);
 
        const pageNumber = `Page ${pdf.internal.getNumberOfPages()}`;
        pdf.setFontSize(10);
        pdf.text(pageNumber, pageWidth - margins - 20, pageHeight - margins);
      }
 
      const date = new Date().toLocaleDateString();
      pdf.text(`Generated on: ${date}`, margins, pageHeight - margins);
      pdf.text(`Total Records: ${allData.length}`, margins, pageHeight - margins - 5);
 
      pdf.save(`bot-report-${date}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };
 
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{
          padding: isMobile ? '8px' : '24px',
          backgroundColor: '#ffffff',
          minHeight: '40vh',
          marginBottom: '100px',
          '@media (min-width: 768px) and (max-width: 1024px)': {
            minHeight: '100vh',
            height: 'auto',
            overflowY: 'auto',
            minHeight: '65vh',
          },
          '@media (min-width: 1024px) and (min-height: 1366px)': {
            height: 'auto',
            minHeight: '80vh',
          },
        }}>
          
       
        <Box sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Typography
            variant="h2"
            sx={{
              ml: 1,
            }}
          >
            Report : Bot Activity Log Report {count > 0 ? `(${count})` : ""}
          </Typography>

         
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadClick}
              endIcon={<MoreVertIcon />}
              size={isMobile ? "small" : "medium"}
              disabled={!hasSearched || botData.length === 0}
              sx={{ textTransform: 'none', fontSize: '15px' }}
            >
              Download
            </Button>

            <Menu
              anchorEl={downloadAnchorEl}
              open={Boolean(downloadAnchorEl)}
              onClose={handleDownloadClose}
            >
              <MenuItem onClick={handleDownloadExcel}>Excel</MenuItem>
              <MenuItem onClick={handleDownloadCSV}>CSV</MenuItem>
              <MenuItem onClick={handleDownloadPDF}>PDF</MenuItem>
            </Menu>
          </Box>
        </Box>
         <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>

        <Box sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: 2,
          alignItems: 'flex-end',
          marginBottom: '12px',
          flexWrap: 'wrap',
          backgroundColor: 'white',
          borderRadius: '8px',
          marginLeft: '8px'
        }}>

          <Box sx={{
            width: isMobile ? '100%' : '230px',
            minWidth: isMobile ? '100%' : '230px',
            minHeight: '85px'
          }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px', fontFamily: '"Poppins", sans-serif' }}>
              From Date <span style={{ color: 'red' }}>*</span>
            </Typography>
            <DatePickerField
              fieldName="fromDate"
              formData={formData}
              handleDateChange={handleDateChange}
              handleBlur={handleBlur}
              touchedFields={touchedFields}
              fieldErrors={fieldErrors}
              size={isMobile ? "small" : "medium"}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  height: '55px'
                }
              }}
            />
          </Box>

          <Box sx={{
            width: isMobile ? '100%' : '230px',
            minWidth: isMobile ? '100%' : '230px',
            minHeight: '85px'
          }}>
            <Typography variant="subtitle2" sx={{ marginBottom: '4px', fontFamily: '"Poppins", sans-serif' }}>
              To Date <span style={{ color: 'red' }}>*</span>
            </Typography>
            <DatePickerField
              fieldName="toDate"
              formData={formData}
              handleDateChange={handleDateChange}
              handleBlur={handleBlur}
              touchedFields={touchedFields}
              fieldErrors={fieldErrors}
              size={isMobile ? "small" : "medium"}
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  height: '55px'
                }
              }}
            />
          </Box>

          <Box sx={{
            width: isMobile ? '100%' : '200px',
            minWidth: isMobile ? '100%' : '200px',
            minHeight: '85px'
          }}>
            <Typography variant="subtitle2" 
            sx={{ marginBottom: '4px', fontFamily: '"Poppins", sans-serif' }}>
              Search Term
            </Typography>
            <TextField
              fullWidth
              size={isMobile ? "small" : "medium"}
              variant="outlined"
              placeholder="Enter search term"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                '& .MuiInputBase-root': {
                  height: '55px'
                }
              }}
            />
          </Box>
         
          <Box sx={{
            display: 'flex',
            gap: 2,
            mb: "5px",
            width: isMobile ? '100%' : 'auto',
            minHeight: '55px',
            alignItems: 'center',
            '& button': {
              flex: isMobile ? 1 : 'none',
              height: '55px',
              minHeight: '55px'
            }
          }}>
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={!((formData.fromDate && formData.toDate) || search.trim())}

          size={isMobile ? "small" : "medium"}
        >
          Search
        </Button>
            
        <Button
          onClick={resetSearch}
          size={isMobile ? "small" : "medium"}
          variant="outlined"
        >
          Reset
        </Button>
      </Box>
    </Box>
  </Box>
 
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 2 }}>
            <Loader />
          </Box>
        ) : !hasSearched ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              mt: 2
            }}
          >
            <NoDataFound discription={"Please use the search filters above to fetch and display updated Activity data."} />
          </Box>
        ) : botData.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
              backgroundColor: '#f9f9f9',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              mt: 2
            }}
          >
            <NoDataFound discription={"No data found for the selected criteria."} />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: isMobile ? '60vh' : 'none',
              overflow: 'auto',
              width: '100%',
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              mt: 2
            }}
          >
            <Table
              className={classes.table}
              sx={{
                '& .MuiTableCell-root': {
                  fontSize: isMobile ? '12px' : '14px',
                  padding: isMobile ? '8px' : '12px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isMobile ? '100px' : '200px',
                  fontFamily: '"Poppins", sans-serif',
                }
              }}
              size={isMobile ? "small" : "medium"}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("carrierName")}
                  >
                    Carrier Name{" "}
                    {sortField === "carrierName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("botProcessMasterName")}
                  >
                    Process Master Name{" "}
                    {sortField === "botProcessMasterName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("trackingDate")}
                  >
                    Track Date{" "}
                    {sortField === "trackingDate" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("policyNumber")}
                  >
                    Policy Number{" "}
                    {sortField === "policyNumber" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("botSubProcessName")}
                  >
                    Sub Process Name{" "}
                    {sortField === "botSubProcessName" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("resultText")}
                  >
                    Text Successful{" "}
                    {sortField === "resultText" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("resultTask")}
                  >
                    Result Successful{" "}
                    {sortField === "resultTask" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                  <TableCell
                    className="styled-table-heading"
                    sx={{
                      fontSize: isMobile ? '12px' : '14px',
                      padding: isMobile ? '8px' : '12px',
                      cursor: 'pointer'
                    }}
                    onClick={() => handleSort("createdAt")}
                  >
                    Date Created{" "}
                    {sortField === "createdAt" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {botData.map((row, index) => (
                  <TableRow
                    key={row.id || index}
                    className="styled-table-row"
                    sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}
                  >
                    <TableCell className={classes.tableCellData}>
                      {row.carrierName || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.botProcessMasterName || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.trackingDate || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.policyNumber || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.botSubProcessName || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.resultText || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.resultTask || ''}
                    </TableCell>
                    <TableCell className={classes.tableCellData}>
                      {row.createdAt ? dayjs(row.createdAt).format("MM-DD-YYYY, hh:mm:ss A") : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {count > 0 && hasSearched && (
          <Box display="flex" justifyContent="flex-end" alignItems="center" p={2} sx={{
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Box display="flex" alignItems="center" sx={{
              marginRight: isMobile ? 0 : 2,
              marginBottom: isMobile ? 2 : 0
            }}>
              <Typography variant="body2" sx={{ marginRight: 1, fontFamily: '"Poppins", sans-serif' }}>
                Rows per page:
              </Typography>
              <FormControl variant="outlined" size="small" sx={{ minWidth: 70 }}>
                <Select
                  name="rowsPerPage"
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{
                    fontSize: '14px',
                    height: 32,
                    fontFamily: '"Poppins", sans-serif'
                  }}
                >
                  {[5, 10, 15, 25, 50, 100].map((option) => (
                    <MenuItem key={option} value={option} sx={{ fontFamily: '"Poppins", sans-serif' }}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Pagination
              count={Math.ceil(count / rowsPerPage)}
              page={page + 1}
              onChange={(e, value) => {
                setPage(value - 1);
                dispatch(searchBotLogsReportAction({
                  fromDate: formData.fromDate,
                  toDate: formData.toDate,
                  search,
                }, value - 1, rowsPerPage));
              }}
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontFamily: '"Poppins", sans-serif'
                }
              }}
            />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}
 
export default BotReport;
 
const getBotData = (bots) => {
  if (!Array.isArray(bots)) {
    return [];
  }
  return bots;
};


